/**
 * Fees and tick offers of token pages (Bez, 2026-10-09: "listing a token should be free; only giving the badge should cost").
 * Pure, no React. Every fee is read from the API's admin settings, never written into the page: 0 wei is "Free".
 *
 * Blue tick: the wallet that controls the token (its owner, or its deployer when ownership is renounced) proves it; with a fee the
 * proof runs again once the payment is final. Gold tick: the CADINU team reviews the project by hand; the fee pays for the review,
 * never for the tick. Neither is an audit.
 */
import type { ControlAnswer, Fee, ProjectInfoResponse, TickKind, TickRequestRow, TokenPagesSettings } from './types'

export const isFree = (fee?: Partial<Fee> | null): boolean => fee?.wei === '0'

/** "Free" for 0 wei, else "<bnb> BNB"; null while unknown */
export function feeText(fee?: Partial<Fee> | null): string | null {
  if (!fee || typeof fee.wei !== 'string') return null
  return isFree(fee) ? 'Free' : `${fee.bnb} BNB`
}

/** the submit page's steps: a free submission has no Pay step */
export const submitSteps = (free: boolean) => ['Details', free ? 'Review' : 'Pay', 'Status']

export type OfferStatus = 'granted' | 'open' | 'available' | 'wrong_wallet' | 'no_controller' | 'not_team' | 'closed' | 'unavailable'
export type TickOffer = { kind: TickKind; status: OfferStatus; fee: string | null; free: boolean; who?: string | null; requestId?: string }

const lc = (a?: string | null) => (a ?? '').toLowerCase()
const OPEN: ReadonlyArray<string> = ['awaiting_payment', 'payment_pending', 'in_review']

/** Wallets that speak for the token's approved info without asking the chain: its submitter and its confirmed members. */
export function teamWallets(r?: ProjectInfoResponse | null): string[] {
  const i = r?.info
  if (!i) return []
  return [i.submittedBy, ...(i.members ?? []).map((m) => m.wallet)].filter(Boolean).map((w) => lc(w))
}

/**
 * What the tick panel offers `wallet` for one tick: granted, an open request of this wallet, available (with the fee), or why not.
 * `control` is the API's control check (the one wallet that may prove control), null while unknown.
 */
export function tickOffer(
  kind: TickKind,
  { settings, info, control, requests, wallet }: { settings?: TokenPagesSettings | null; info?: ProjectInfoResponse | null; control?: ControlAnswer | null; requests?: TickRequestRow[] | null; wallet?: string | null },
): TickOffer {
  const fee = settings?.ticks?.[kind]
  const base = { kind, fee: feeText(fee), free: isFree(fee) }
  if (!info?.info || !settings?.ticks || !fee) return { ...base, status: 'unavailable' }
  if (kind === 'blue' ? info.blueVerified === true : info.cadinuVerified === true) return { ...base, status: 'granted' }
  const open = (requests ?? []).find((q) => q.kind === kind && lc(q.token) === lc(info.token) && OPEN.includes(q.status))
  if (open) return { ...base, status: 'open', requestId: open.id }
  if (!settings.ticks.open) return { ...base, status: 'closed' }
  const w = lc(wallet)
  const controller = control ? lc(control.wallet) : ''
  if (kind === 'blue') {
    if (control && !control.wallet) return { ...base, status: 'no_controller' }
    if (!w || w !== controller) return { ...base, status: 'wrong_wallet', who: control?.wallet ?? null }
    return { ...base, status: 'available' }
  }
  if (w && (teamWallets(info).includes(w) || w === controller)) return { ...base, status: 'available' }
  return { ...base, status: 'not_team' }
}
